import React, { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import styles from "../../styles/crearEmpresaPopup.module.css";
import { useTranslation } from "react-i18next";
import Loader from "../Loader";
import useFetch from "../../hooks/useFetch";
import ReactivarEncuestaPopup from "./ReactivarEncuestaPopup";

const EncuestasAnterioresPopup = ({ onClose, idEquipo, idEmpresa, onReactivar }) => {
  const { t } = useTranslation();
  const [encuestas, setEncuestas] = useState([]);
  const [encuestaAReactivar, setEncuestaAReactivar] = useState(null);
  const { data, execute, loading } = useFetch();
  const executeRef = useRef(execute);

  useEffect(() => {
    executeRef.current = execute;
  }, [execute]);

  useEffect(() => {
    const fetchEncuestas = async () => {
      if (!idEquipo || !idEmpresa) return;

      try {
        const apiUrl = process.env.REACT_APP_API_URL || "https://api.systemicsurvey.com";
        await executeRef.current(`${apiUrl}/gestionEncuestas/getEncuestasAnteriores`, "POST", {
          id_equipo: idEquipo,
          id_empresa: idEmpresa,
        });
      } catch (error) {
        console.error("Error al obtener encuestas anteriores:", error);
        setEncuestas([]);
      }
    };

    fetchEncuestas();
  }, [idEquipo, idEmpresa]);

  useEffect(() => {
    if (data && data.encuestas) {
      setEncuestas(data.encuestas || []);
    } else if (data && data.encuestas === undefined) {
      // Si la respuesta no tiene el formato esperado, intentar usar data directamente
      setEncuestas(Array.isArray(data) ? data : []);
    }
  }, [data]);

  const handleClickOutside = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const getEstadoColor = (estado) => {
    switch (estado) {
      case "finalizada":
        return "#28a745";
      case "programada":
        return "#ffc107";
      case "cancelada":
        return "#dc3545";
      default:
        return "#6c757d";
    }
  };

  const getEstadoText = (estado) => {
    switch (estado) {
      case "finalizada":
        return t("finalizada");
      case "programada":
        return t("programada");
      case "cancelada":
        return t("cancelada");
      default:
        return estado;
    }
  };

  return ReactDOM.createPortal(
    <div className={styles.popupOverlay} onClick={handleClickOutside}>
      <div className={styles.popupContent} style={{ maxWidth: "1000px", maxHeight: "85vh", overflowY: "auto", overflowX: "hidden" }}>
        <h2 style={{ marginBottom: "1.5rem", marginTop: 0 }}>{t("encuestasAnteriores")}</h2>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "2rem 0" }}>
            <Loader />
          </div>
        ) : encuestas.length === 0 ? (
          <p style={{ textAlign: "center", color: "#6c757d", padding: "2rem 0" }}>
            {t("noEncuestasAnteriores")}
          </p>
        ) : (
          <div style={{ marginBottom: "1.5rem" }}>
            <table className={styles.equiposTable} style={{ width: "100%", margin: 0 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "center", padding: "12px" }}>{t("fechaInicio")}</th>
                  <th style={{ textAlign: "center", padding: "12px" }}>{t("fechaFin")}</th>
                  <th style={{ textAlign: "center", padding: "12px" }}>{t("estado")}</th>
                  <th style={{ textAlign: "center", padding: "12px" }}>{t("acciones")}</th>
                </tr>
              </thead>
              <tbody>
                {encuestas.map((encuesta, index) => (
                  <tr key={encuesta.id_encuesta || index}>
                    <td style={{ textAlign: "center", padding: "12px" }}>{encuesta.fecha_inicio || "-"}</td>
                    <td style={{ textAlign: "center", padding: "12px" }}>{encuesta.fecha_fin || "-"}</td>
                    <td style={{ textAlign: "center", padding: "12px" }}>
                      <span
                        style={{
                          padding: "4px 12px",
                          borderRadius: "12px",
                          fontSize: "0.875rem",
                          fontWeight: "500",
                          backgroundColor: `${getEstadoColor(encuesta.estado)}20`,
                          color: getEstadoColor(encuesta.estado),
                        }}
                      >
                        {getEstadoText(encuesta.estado)}
                      </span>
                    </td>
                    <td style={{ textAlign: "center", padding: "12px" }}>
                      {encuesta.estado && encuesta.estado.toLowerCase() === "finalizada" && (
                        <button
                          onClick={() => setEncuestaAReactivar(encuesta)}
                          style={{
                            padding: "6px 16px",
                            backgroundColor: "var(--color-primario)",
                            color: "white",
                            border: "none",
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontSize: "0.875rem",
                            fontWeight: "500",
                            transition: "background-color 0.3s",
                          }}
                          onMouseOver={(e) => e.target.style.backgroundColor = "#c1125f"}
                          onMouseOut={(e) => e.target.style.backgroundColor = "var(--color-primario)"}
                        >
                          {t("ver")}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className={styles.popupActions} style={{ marginTop: "1.5rem" }}>
          <button onClick={onClose} className={styles.buttonCancel}>
            {t("close")}
          </button>
        </div>
      </div>
      {encuestaAReactivar && (
        <ReactivarEncuestaPopup
          onClose={() => setEncuestaAReactivar(null)}
          encuesta={encuestaAReactivar}
          idEquipo={idEquipo}
          idEmpresa={idEmpresa}
          onConfirm={async (datos) => {
            await onReactivar(datos);
            setEncuestaAReactivar(null);
            // Recargar las encuestas
            const apiUrl = process.env.REACT_APP_API_URL || "https://api.systemicsurvey.com";
            await executeRef.current(`${apiUrl}/gestionEncuestas/getEncuestasAnteriores`, "POST", {
              id_equipo: idEquipo,
              id_empresa: idEmpresa,
            });
          }}
        />
      )}
    </div>,
    document.body
  );
};

export default EncuestasAnterioresPopup;

